import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef } from 'react';

import Button from 'shared/components/button/Button';
import CloseButton from 'shared/components/button/CloseButton';
import ExpandButton from 'shared/components/button/ExpandButton';

import styles from './Tabs.module.scss';

const Tabs = ({
  items,
  selectedId,
  showExpanded,
  className = '',
  closeHandler,
  clickHandler,
  theme = 'dark',
  toggleHandler,
}) => {
  const ref = useRef();

  const handleClickOutside = useCallback(
    e => {
      if (e && ref.current && showExpanded && !ref.current.contains(e.target)) {
        e.stopPropagation();
        toggleHandler();
      }
    },
    [showExpanded, toggleHandler],
  );

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [handleClickOutside]);

  const selectedItem = items.find(item => item.id === selectedId) || '';
  const title = selectedItem.menuText || '';
  const fullWidth = !closeHandler;

  return (
    <div
      className={classnames(
        styles.wrapper,
        {
          [styles.dark]: theme === 'dark',
          [styles.light]: theme === 'light',
          [styles.mobileActive]: showExpanded,
        },
        className,
      )}
    >
      <div className={styles.container} ref={ref}>
        <ExpandButton
          fullWidth={fullWidth}
          isActive={showExpanded}
          onClick={toggleHandler}
          theme={theme}
          title={title}
        />

        <ul
          className={classnames(styles.items, {
            [styles.fullWidth]: fullWidth,
          })}
        >
          {items
            .filter(item => !showExpanded || item.id !== selectedId)
            .map(item => (
              <li key={item.id}>
                <Button
                  className={classnames(styles.button, {
                    [styles.active]: item.id === selectedId,
                  })}
                  onClick={() => clickHandler(item.id)}
                >
                  {item.menuText}
                </Button>
              </li>
            ))}
        </ul>
      </div>

      {!fullWidth && (
        <CloseButton muted={showExpanded} onClick={closeHandler} />
      )}
    </div>
  );
};

Tabs.propTypes = {
  className: PropTypes.string,
  clickHandler: PropTypes.func.isRequired,
  closeHandler: PropTypes.func,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      menuText: PropTypes.string.isRequired,
    }),
  ).isRequired,
  toggleHandler: PropTypes.func.isRequired,
  selectedId: PropTypes.string.isRequired,
  showExpanded: PropTypes.bool.isRequired,
  theme: PropTypes.oneOf(['dark', 'light']),
};

export default Tabs;
